<template>
  <div>
    <!-- Help Panel -->
    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="opacity-0 translate-x-full"
      enter-to-class="opacity-100 translate-x-0"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="opacity-100 translate-x-0"
      leave-to-class="opacity-0 translate-x-full"
    >
      <div
        v-if="isOpen"
        class="fixed top-0 right-0 bottom-0 w-96 bg-white shadow-2xl z-50 flex flex-col"
      >
        <!-- Header -->
        <div class="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 flex-shrink-0">
          <div class="flex items-center justify-between mb-2">
            <h2 class="text-2xl font-bold">{{ currentPageHelp.title }}</h2>
            <button
              @click="isOpen = false"
              class="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
            >
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
              </svg>
            </button>
          </div>
          <p class="text-blue-100 text-sm">{{ currentPageHelp.subtitle }}</p>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-y-auto">
          <!-- Best Practices Banner (if applicable) -->
          <div v-if="showBestPractices" class="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-200">
            <div class="flex items-start gap-3">
              <div class="flex-shrink-0 mt-0.5">
                <svg class="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              </div>
              <div class="flex-1">
                <h4 class="font-semibold text-blue-900 mb-2">💡 Best Practice: Keeping Your Portfolio in Sync</h4>
                <p class="text-sm text-blue-800 mb-3">
                  To get the most accurate AI recommendations and portfolio insights, <strong>keep InvestBuddy.ai synchronized with your actual broker holdings</strong>:
                </p>
                <ul class="text-sm text-blue-800 space-y-2 ml-4">
                  <li class="flex items-start gap-2">
                    <span class="text-blue-600 font-bold mt-0.5">1.</span>
                    <div>
                      <strong>Initial Import:</strong> Export your holdings from your broker (CSV format) and import them here. Supported brokers: Interactive Brokers, Robinhood, Schwab, Fidelity, E*TRADE, TD Ameritrade.
                    </div>
                  </li>
                  <li class="flex items-start gap-2">
                    <span class="text-blue-600 font-bold mt-0.5">2.</span>
                    <div>
                      <strong>After Each Trade:</strong> When you buy/sell stocks in your broker, manually record the same transaction in InvestBuddy.ai to keep it in sync.
                    </div>
                  </li>
                  <li class="flex items-start gap-2">
                    <span class="text-blue-600 font-bold mt-0.5">3.</span>
                    <div>
                      <strong>Review Recommendations:</strong> Use InvestBuddy.ai's AI to research and make decisions, then execute the actual trades in your broker account.
                    </div>
                  </li>
                  <li class="flex items-start gap-2">
                    <span class="text-blue-600 font-bold mt-0.5">4.</span>
                    <div>
                      <strong>Periodic Re-import:</strong> Re-import your broker CSV weekly or monthly to ensure everything stays accurate.
                    </div>
                  </li>
                </ul>
                <div class="mt-3 pt-3 border-t border-blue-200">
                  <p class="text-xs text-blue-700">
                    <strong>⚠️ Note:</strong> Direct broker integration isn't available (most brokers don't provide public APIs). The CSV import + manual sync workflow is currently the best way to use InvestBuddy.ai alongside your broker.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Page Overview -->
          <div class="p-6 border-b border-gray-200">
            <h3 class="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <span class="text-2xl">{{ currentPageHelp.icon }}</span>
              <span>What This Page Offers</span>
            </h3>
            <div class="prose prose-sm text-gray-700" v-html="currentPageHelp.description"></div>
          </div>

          <!-- How to Use -->
          <div class="p-6 border-b border-gray-200">
            <h3 class="font-semibold text-gray-900 mb-4">How to Use This Page</h3>
            <div class="space-y-4">
              <div
                v-for="(step, index) in currentPageHelp.howToUse"
                :key="index"
                class="flex gap-4"
              >
                <div class="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center font-semibold text-sm">
                  {{ index + 1 }}
                </div>
                <div class="flex-1 pt-0.5">
                  <p class="text-gray-800 font-medium mb-1">{{ step.title }}</p>
                  <p class="text-sm text-gray-600">{{ step.description }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Tips & Tricks -->
          <div v-if="currentPageHelp.tips?.length" class="p-6 border-b border-gray-200">
            <h3 class="font-semibold text-gray-900 mb-4">💡 Tips & Tricks</h3>
            <div class="space-y-3">
              <div
                v-for="(tip, index) in currentPageHelp.tips"
                :key="index"
                class="flex gap-3 p-3 bg-blue-50 rounded-lg"
              >
                <span class="text-blue-600 flex-shrink-0">💡</span>
                <p class="text-sm text-gray-700">{{ tip }}</p>
              </div>
            </div>
          </div>

          <!-- Common Questions -->
          <div v-if="currentPageHelp.faq?.length" class="p-6">
            <h3 class="font-semibold text-gray-900 mb-4">Common Questions</h3>
            <div class="space-y-2">
              <div
                v-for="(item, index) in currentPageHelp.faq"
                :key="index"
                class="border border-gray-200 rounded-lg overflow-hidden"
              >
                <button
                  @click="toggleItem(index)"
                  class="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                >
                  <span class="font-medium text-gray-900">{{ item.question }}</span>
                  <svg
                    class="w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ml-2"
                    :class="{ 'rotate-180': expandedItems.includes(index) }"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <Transition
                  enter-active-class="transition duration-200 ease-out"
                  enter-from-class="opacity-0"
                  enter-to-class="opacity-100"
                  leave-active-class="transition duration-150 ease-in"
                  leave-from-class="opacity-100"
                  leave-to-class="opacity-0"
                >
                  <div
                    v-if="expandedItems.includes(index)"
                    class="px-4 pb-4 text-sm text-gray-700 bg-gray-50"
                  >
                    {{ item.answer }}
                  </div>
                </Transition>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Backdrop -->
    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isOpen"
        @click="isOpen = false"
        class="fixed inset-0 bg-black bg-opacity-25 z-45"
      ></div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'

const route = useRoute()

// Support v-model for external control
const props = defineProps<{
  modelValue?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
}>()

// Use v-model if provided, otherwise use internal state
const isOpen = computed({
  get: () => props.modelValue ?? false,
  set: (value) => emit('update:modelValue', value)
})

const expandedItems = ref<number[]>([])

interface HowToStep {
  title: string
  description: string
}

interface FAQ {
  question: string
  answer: string
}

interface PageHelp {
  title: string
  subtitle: string
  icon: string
  description: string
  howToUse: HowToStep[]
  tips?: string[]
  faq?: FAQ[]
  showBestPractices?: boolean
}

// Define help content for each page
const pageHelpContent: Record<string, PageHelp> = {
  dashboard: {
    title: 'Dashboard',
    subtitle: 'Your portfolio overview at a glance',
    icon: '📊',
    description: `
      <p class="mb-2">The Dashboard provides a comprehensive overview of your entire investment portfolio in one place.</p>
      <p>View your total portfolio value, day's performance, asset allocation, top performers, and get AI-powered insights about your investments.</p>
    `,
    howToUse: [
      {
        title: 'Monitor Performance',
        description: 'View your total portfolio value, today\'s gains/losses, and percentage changes at the top of the page.'
      },
      {
        title: 'Check Asset Allocation',
        description: 'See how your investments are distributed across different asset types (stocks, ETFs, etc.).'
      },
      {
        title: 'Review Top Holdings',
        description: 'Identify your best and worst performing stocks to make informed decisions.'
      },
      {
        title: 'Get AI Insights',
        description: 'Read personalized recommendations and market insights powered by our AI engine.'
      }
    ],
    tips: [
      'Check your dashboard daily during market hours to stay informed about your portfolio performance.',
      'The portfolio summary updates in real-time during market hours (9:30 AM - 4:00 PM ET).',
      'Green/red indicators show gains/losses - hover for detailed information.'
    ],
    showBestPractices: false
  },
  
  portfolio: {
    title: 'Portfolio',
    subtitle: 'Manage your holdings and track performance',
    icon: '💼',
    description: `
      <p class="mb-2">Manage all your stock holdings, view detailed performance metrics, and import your portfolio from your broker.</p>
      <p>This is your central hub for tracking what you own, how it\'s performing, and making adjustments to your investments.</p>
    `,
    howToUse: [
      {
        title: 'Import from Your Broker',
        description: 'Click "Import from CSV" and upload your broker statement. We support Interactive Brokers, Robinhood, Schwab, E*TRADE, Fidelity, and more.'
      },
      {
        title: 'Add Holdings Manually',
        description: 'Click "Add Holding" to manually enter stocks. Specify symbol, quantity, and average cost.'
      },
      {
        title: 'Track Performance',
        description: 'Each holding shows current price, total value, gain/loss, and percentage change.'
      },
      {
        title: 'Export for Records',
        description: 'Click "Export CSV" to download your complete portfolio for tax reporting or backup.'
      }
    ],
    tips: [
      'Use CSV import to save time - it automatically adds all your holdings with accurate cost basis.',
      'Buying power only decreases when you manually BUY stocks, not when importing existing holdings.',
      'Set price alerts on individual holdings to get notified of significant price movements.',
      'The system uses FIFO (First-In-First-Out) for accurate tax calculations.',
      'Check the Portfolio 10-Day Prediction section to see ML-powered forecasts for your entire portfolio value.',
      'Click on individual stocks in the prediction section to view day-by-day price predictions for the next 10 days.'
    ],
    faq: [
      {
        question: 'Why didn\'t my buying power change after CSV import?',
        answer: 'Importing existing holdings doesn\'t affect buying power because you\'ve already paid for those stocks. Buying power only decreases when you execute new purchases through the platform.'
      },
      {
        question: 'How do I keep InvestBuddy.ai in sync with my broker?',
        answer: 'After each trade you make in your broker account, manually record the same buy/sell transaction here. Alternatively, re-import your broker CSV weekly/monthly to refresh all holdings at once.'
      },
      {
        question: 'Can InvestBuddy.ai connect directly to my broker?',
        answer: 'Not currently. Most brokers don\'t provide public APIs for retail users. The recommended workflow is: (1) Import broker CSV initially, (2) Use InvestBuddy.ai for research and AI recommendations, (3) Execute trades in your broker, (4) Manually record transactions here to stay in sync.'
      },
      {
        question: 'Can I track options or crypto?',
        answer: 'Currently, InvestBuddy.ai focuses on stocks and ETFs. Options and cryptocurrency support is planned for future releases.'
      },
      {
        question: 'What is Portfolio 10-Day Prediction?',
        answer: 'This Premium feature uses our LSTM neural network to forecast your entire portfolio\'s value over the next 10 days. It analyzes each holding and provides predicted prices, helping you anticipate portfolio value changes. Click on any stock in the Individual Stock Predictions section to see day-by-day price forecasts. This helps you make informed decisions about rebalancing or timing trades.'
      },
      {
        question: 'How accurate is the Portfolio 10-Day Prediction?',
        answer: 'Our LSTM models achieve 55.41% OOS win rate in real backtesting (241 trades, 2022-2025, monthly rebalancing). This resulted in +63.41% alpha over the S&P 500. The AI correctly predicted profitable trades over half the time in our comprehensive testing on 58 stocks. Individual stock predictions vary - view confidence scores for each holding. Remember: these are forecasts based on historical patterns, not guarantees.'
      }
    ],
    showBestPractices: true
  },
  
  recommendations: {
    title: 'AI Recommendations',
    subtitle: 'Personalized stock suggestions from our AI',
    icon: '🤖',
    description: `
      <p class="mb-2">Get AI-powered stock recommendations tailored to your portfolio, risk profile, and financial goals.</p>
      <p>Our machine learning models analyze thousands of stocks daily to suggest investments that complement your existing holdings and align with your objectives.</p>
    `,
    howToUse: [
      {
        title: 'Review Recommendations',
        description: 'Browse AI-suggested stocks with buy/sell/hold ratings, target prices, and confidence scores.'
      },
      {
        title: 'Understand the Rationale',
        description: 'Click on any recommendation to see WHY the AI suggests it - including how it fits your portfolio and goals.'
      },
      {
        title: 'Check Diversification',
        description: 'The AI considers your current holdings to suggest stocks that improve diversification.'
      },
      {
        title: 'Execute Trades in Your Broker',
        description: 'Once you\'ve decided on a recommendation, execute the trade in your broker account, then manually record it in the Portfolio page to keep InvestBuddy.ai in sync.'
      }
    ],
    tips: [
      'Recommendations update daily based on market conditions and your portfolio changes.',
      'Higher confidence scores (80%+) indicate stronger AI conviction.',
      'The AI factors in your risk tolerance, time horizon, and financial goals from your profile.',
      'Use recommendations as research - make the final decision yourself and execute in your broker.',
      'After buying/selling in your broker, remember to record the transaction in Portfolio to keep InvestBuddy.ai accurate.'
    ],
    faq: [
      {
        question: 'How accurate are the AI predictions?',
        answer: 'In our comprehensive backtest (2022-2025), our AI achieved a 55.41% OOS win rate with monthly rebalancing across 241 trades. The model beat the S&P 500 by +63.41% over this 4-year period. This is actual trading simulation accuracy, including transaction costs and realistic market conditions—not just directional prediction accuracy.'
      },
      {
        question: 'What does "directional accuracy" mean?',
        answer: 'Directional accuracy measures whether our AI correctly predicted if a stock price would go UP or DOWN over the next 10 days. It does NOT measure: (1) how much the price moved, (2) profit after transaction fees, or (3) beating the market. Think of it as "was the AI right about the direction?" not "did I make money?"'
      },
      {
        question: 'How was the model validated?',
        answer: 'Through comprehensive walk-forward backtesting on 58 stocks (2022-2025). The Phase 2 model was trained on 77 symbols (13,014 sequences) and validated out-of-sample on 85 symbols — including 8 completely unseen semiconductor stocks it had never seen during training (zero-shot generalization). We simulated real trading with monthly rebalancing, $1 transaction fees, and 0.05% slippage. Results: 55.41% OOS win rate, +63.41% alpha over S&amp;P 500, 0.95 Sharpe ratio. This is industry-standard validation methodology.'
      },
      {
        question: 'Can I expect 55.41% win rate on my trades?',
        answer: 'Individual results will vary. The 55.41% represents our OOS backtest performance on 58 stocks during 2022-2025 (includes bear and bull markets). Your results depend on: which stocks you choose, when you buy/sell, market conditions, transaction costs, and how you size positions. Past performance does not guarantee future results. See our benchmarks page for full transparency.'
      },
      {
        question: 'What technology powers the recommendations?',
        answer: 'We use LSTM (Long Short-Term Memory) neural networks enhanced with market correlation features. The models analyze 17 different factors including technical indicators and how stocks correlate with major indices (S&P 500, NASDAQ). Models are continuously updated with new market data.'
      },
      {
        question: 'How often do recommendations change?',
        answer: 'Recommendations are updated every 24 hours. As market conditions, stock prices, and your portfolio change, the AI may adjust its suggestions.'
      },
      {
        question: 'Why does the AI recommend selling stocks I own?',
        answer: 'The AI may suggest selling if a stock has reached its target price, fundamentals have deteriorated, or better opportunities exist elsewhere. Always review the rationale before making decisions.'
      },
      {
        question: 'Can I customize what the AI recommends?',
        answer: 'Yes! Update your risk profile, financial goals, and preferences in Settings. The AI uses this information to tailor recommendations specifically for you.'
      }
    ],
    showBestPractices: true
  },
  
  watchlist: {
    title: 'Watchlist',
    subtitle: 'Track stocks you\'re interested in',
    icon: '👁️',
    description: `
      <p class="mb-2">Monitor stocks you\'re considering for investment without actually buying them yet.</p>
      <p>Set price alerts, track performance, and build a list of potential investments for when you\'re ready to buy.</p>
    `,
    howToUse: [
      {
        title: 'Add Stocks',
        description: 'Click "Add to Watchlist" and search for any stock symbol. Add as many as you want to track.'
      },
      {
        title: 'Monitor Prices',
        description: 'See real-time prices, daily changes, and performance metrics for all watched stocks.'
      },
      {
        title: 'Set Price Alerts',
        description: 'Click the bell icon to set alerts. Get notified when a stock reaches your target price.'
      },
      {
        title: 'Research & Buy',
        description: 'Click any stock to see detailed analysis, charts, and news. Buy directly when ready.'
      }
    ],
    tips: [
      'Use watchlists to practice "paper trading" - track how stocks perform before risking real money.',
      'Set both high and low price alerts to catch buying opportunities and exit signals.',
      'Prices update in real-time during market hours.',
      'Compare watchlist stocks side-by-side to make better investment decisions.'
    ],
    showBestPractices: false
  },
  
  analytics: {
    title: 'Analytics',
    subtitle: 'Deep dive into your portfolio metrics',
    icon: '📈',
    description: `
      <p class="mb-2">Advanced analytics and visualizations to understand your portfolio\'s performance, risk, and trends.</p>
      <p>View historical performance, sector allocation, risk metrics, and compare your returns against market benchmarks.</p>
    `,
    howToUse: [
      {
        title: 'View Performance Charts',
        description: 'Analyze your portfolio value over time with interactive charts. Switch between daily, monthly, and yearly views.'
      },
      {
        title: 'Check Sector Allocation',
        description: 'See how your investments are distributed across technology, healthcare, finance, and other sectors.'
      },
      {
        title: 'Review Risk Metrics',
        description: 'Understand your portfolio\'s volatility, beta, Sharpe ratio, and other risk indicators.'
      },
      {
        title: 'Compare to Benchmarks',
        description: 'See how your portfolio performs compared to the S&P 500 and other market indices.'
      }
    ],
    tips: [
      'A well-diversified portfolio typically has exposure to 5-10 different sectors.',
      'High concentration in one sector increases risk - consider rebalancing if over 30% is in a single sector.',
      'Check your risk score regularly - it should align with your risk tolerance.',
      'Export historical data for detailed Excel analysis or tax reporting.'
    ],
    showBestPractices: false
  },
  
  chat: {
    title: 'Financial Assistant',
    subtitle: 'Ask questions about investing and your portfolio',
    icon: '💬',
    description: `
      <p class="mb-2">Chat with our AI financial assistant to get instant answers about investing, your portfolio, and market conditions.</p>
      <p>Ask anything from basic investing concepts to complex portfolio analysis questions.</p>
      <p class="mt-2 text-sm text-amber-600"><strong>⚠️ Note:</strong> The chat assistant is still in learning mode and might not understand all requests. For best results, ask clear, specific questions about stocks, portfolio strategy, or investing concepts.</p>
    `,
    howToUse: [
      {
        title: 'Ask Questions',
        description: 'Type any question about investing, stocks, your portfolio, or financial concepts.'
      },
      {
        title: 'Get Personalized Answers',
        description: 'The AI analyzes your portfolio data to provide personalized, contextual responses.'
      },
      {
        title: 'Explore Topics',
        description: 'Ask follow-up questions to dive deeper into any topic. The AI remembers conversation context.'
      },
      {
        title: 'Request Analysis',
        description: 'Ask the AI to analyze specific stocks, compare options, or evaluate portfolio strategies.'
      }
    ],
    tips: [
      'Be specific in your questions for better answers (e.g., "Should I sell AAPL?" vs "What should I do?").',
      'Ask for explanations if financial terms are confusing.',
      'The AI can help you understand why certain stocks are recommended for your portfolio.',
      'Use the chat to learn about investing strategies and concepts.'
    ],
    showBestPractices: false
  },
  
  alerts: {
    title: 'Price Alerts',
    subtitle: 'Get notified of important price movements',
    icon: '🔔',
    description: `
      <p class="mb-2">Set up custom price alerts to get notified when stocks reach your target prices.</p>
      <p>Never miss buying opportunities or exit points with real-time email and in-app notifications.</p>
    `,
    howToUse: [
      {
        title: 'Create Alerts',
        description: 'Click "New Alert", select a stock, set your target price, and choose alert type (above/below).'
      },
      {
        title: 'Get Notified',
        description: 'Receive instant notifications via email and in-app when your price targets are hit.'
      },
      {
        title: 'Manage Alerts',
        description: 'View, edit, or delete existing alerts. Set multiple alerts per stock for different price levels.'
      },
      {
        title: 'Review History',
        description: 'See which alerts have triggered and when to track your timing and decisions.'
      }
    ],
    tips: [
      'Set both upside and downside alerts to catch all important price movements.',
      'Alerts check prices every 10 seconds during market hours.',
      'Create alerts for support/resistance levels identified in technical analysis.',
      'Use alerts to implement stop-loss strategies without constant monitoring.'
    ],
    showBestPractices: false
  },
  
  onboarding: {
    title: 'Getting Started',
    subtitle: 'Set up your InvestBuddy.ai account',
    icon: '🚀',
    description: `
      <p class="mb-2">Welcome to InvestBuddy.ai! This onboarding wizard helps you set up your profile, risk preferences, and financial goals.</p>
      <p>Complete all steps to get personalized AI recommendations tailored to your investment style.</p>
    `,
    howToUse: [
      {
        title: 'Complete Your Profile',
        description: 'Enter basic information like name, age, and investment experience level.'
      },
      {
        title: 'Set Risk Tolerance',
        description: 'Tell us how comfortable you are with investment risk - conservative, moderate, or aggressive.'
      },
      {
        title: 'Define Financial Goals',
        description: 'Set your investment objectives: retirement, wealth building, income generation, etc.'
      },
      {
        title: 'Import Portfolio (Optional)',
        description: 'Upload your broker CSV to import existing holdings, or skip and add them later.'
      }
    ],
    tips: [
      'Be honest about your risk tolerance - it directly affects AI recommendations.',
      'You can always update your profile and goals later in Settings.',
      'Completing onboarding unlocks personalized recommendations and insights.',
      'The more accurate your profile, the better the AI can help you.'
    ],
    showBestPractices: false
  },
  
  pricing: {
    title: 'Premium Subscription',
    subtitle: 'Upgrade for advanced features',
    icon: '⭐',
    description: `
      <p class="mb-2">Compare Free and Premium plans to see which features best fit your investment needs.</p>
      <p>Premium unlocks unlimited AI recommendations, advanced analytics, priority support, and more.</p>
    `,
    howToUse: [
      {
        title: 'Compare Plans',
        description: 'Review the feature comparison table to see what\'s included in Free vs Premium.'
      },
      {
        title: 'Choose Your Plan',
        description: 'Select monthly or annual billing. Annual plans save 20%.'
      },
      {
        title: 'Enter Payment',
        description: 'Securely enter your payment information. We use Stripe for secure processing.'
      },
      {
        title: 'Start Using Premium',
        description: 'Premium features activate immediately after successful payment.'
      }
    ],
    tips: [
      'Annual plans offer significant savings - 2 months free compared to monthly billing.',
      'You can cancel anytime - no long-term commitment required.',
      'Premium users get priority email support with faster response times.',
      'Try the free plan first to see if InvestBuddy.ai fits your needs.'
    ],
    faq: [
      {
        question: 'Can I cancel anytime?',
        answer: 'Yes! You can cancel your Premium subscription at any time. You\'ll keep Premium features until the end of your current billing period.'
      },
      {
        question: 'What\'s the difference between Free and Premium?',
        answer: 'Premium unlocks unlimited AI recommendations (vs 5/month), advanced analytics, detailed risk analysis, sector breakdown, historical performance charts, and priority support.'
      },
      {
        question: 'Is my payment information secure?',
        answer: 'Absolutely. We use Stripe, the industry-leading payment processor. We never store your credit card information on our servers.'
      }
    ],
    showBestPractices: false
  },
  
  stocks: {
    title: 'Stock Search',
    subtitle: 'Research and discover stocks',
    icon: '🔍',
    description: `
      <p class="mb-2">Search for stocks, view detailed information, analyze performance, and add stocks to your portfolio or watchlist.</p>
      <p>Access comprehensive stock data including real-time prices, company profiles, financials, and news.</p>
    `,
    howToUse: [
      {
        title: 'Search for Stocks',
        description: 'Use the search bar to find stocks by symbol (e.g., AAPL) or company name (e.g., Apple).'
      },
      {
        title: 'View Stock Details',
        description: 'Click any stock to see detailed information: price charts, company profile, financial metrics, and recent news.'
      },
      {
        title: 'Analyze Performance',
        description: 'Review historical price charts, P/E ratio, market cap, dividend yield, and other key metrics.'
      },
      {
        title: 'Take Action',
        description: 'Add stocks to your watchlist to monitor them, or add to portfolio if you already own shares.'
      }
    ],
    tips: [
      'Use the search to quickly jump to any stock you\'re researching.',
      'Compare multiple stocks side-by-side by opening them in separate browser tabs.',
      'Check the news section for recent developments that might affect stock price.',
      'Look at both fundamental metrics (P/E, revenue growth) and technical indicators (price trends).'
    ],
    showBestPractices: false
  },
  
  settings: {
    title: 'Settings',
    subtitle: 'Customize your experience and manage your account',
    icon: '⚙️',
    description: `
      <p class="mb-2">Configure your investment preferences, risk profile, notification settings, and account details.</p>
      <p>Personalize InvestBuddy.ai to match your investment style and goals.</p>
    `,
    howToUse: [
      {
        title: 'Update Risk Profile',
        description: 'Set your risk tolerance, investment time horizon, and financial goals. This affects AI recommendations.'
      },
      {
        title: 'Configure Notifications',
        description: 'Choose which alerts and updates you want to receive via email, SMS, or in-app.'
      },
      {
        title: 'Manage Account',
        description: 'Update your email, password, and personal information.'
      },
      {
        title: 'Manage Subscription',
        description: 'View your current plan, upgrade to Premium, or manage billing information.'
      }
    ],
    tips: [
      'Accurate risk profile helps the AI provide better recommendations tailored to you.',
      'Enable email notifications for price alerts so you never miss important movements.',
      'Review and update your financial goals quarterly as your situation changes.',
      'Premium users get advanced analytics, unlimited recommendations, and priority support.',
      'Keep your portfolio in sync by manually updating after each broker trade.'
    ],
    showBestPractices: true
  },
  
  mcplanding: {
    title: 'API & MCP',
    subtitle: 'Connect InvestBuddy AI to ChatGPT and Claude',
    icon: '🔌',
    description: `
      <p class="mb-2">Access InvestBuddy's AI-powered stock predictions and portfolio insights directly in ChatGPT and Claude Desktop.</p>
      <p>Use natural conversation to get 10-day stock forecasts, portfolio analysis, and personalized investment recommendations without switching apps.</p>
    `,
    howToUse: [
      {
        title: 'Get Your API Key',
        description: 'Click "Get API Key" to generate your personal access token. This key is required for both integrations.'
      },
      {
        title: 'Browse the Prompt Library',
        description: 'Visit the Prompt Library to learn the "Core & Explore" methodology—4 daily workflows to maximize your API usage while maintaining optimal monthly rebalancing (+63.41% alpha).'
      },
      {
        title: 'Install for ChatGPT',
        description: 'Click "Install for ChatGPT" to add InvestBuddy as a custom action in ChatGPT. Follow the prompts to paste your API key.'
      },
      {
        title: 'Setup for Claude Desktop',
        description: 'Click "Setup for Claude" to get instructions for adding InvestBuddy as an MCP server in Claude Desktop. You\'ll configure a JSON file with your API key.'
      },
      {
        title: 'Start Using AI Predictions',
        description: 'In ChatGPT or Claude, ask questions like "What\'s your 10-day prediction for AAPL?" or "Analyze my portfolio and suggest improvements."'
      }
    ],
    tips: [
      'Check out the Prompt Library for daily workflows: Daily Risk Manager, Swing Trade Sniper, Earnings Validator, and 401(k) Optimizer.',
      'Use the "Core & Explore" strategy: 80% monthly rebalancing for optimal alpha, 20% active trading with daily API calls.',
      'Your API key is personal and should be kept secure - don\'t share it publicly.',
      'The AI predictions have 55.41% OOS win rate in real backtesting (2022-2025, monthly rebalancing) based on comprehensive walk-forward testing on 58 stocks.',
      'You can ask for multiple stock predictions in one conversation.',
      'Free users get 5 API calls per day. Premium users get 150 calls per day.',
      'Regenerate your API key anytime from the Settings page if it gets compromised.'
    ],
    faq: [
      {
        question: 'What\'s the difference between ChatGPT and Claude integration?',
        answer: 'Both provide the same stock prediction features. ChatGPT uses a custom GPT action (available to ChatGPT Plus users), while Claude Desktop uses the Model Context Protocol (MCP). Choose based on which AI assistant you prefer.'
      },
      {
        question: 'Do I need a premium subscription to use the API?',
        answer: 'No, free users get 5 API calls per day. Premium users get 150 calls per day for $39/month.'
      },
      {
        question: 'How accurate are the 10-day predictions?',
        answer: 'Our models achieve 55.41% OOS win rate in realistic backtesting (2022-2025, monthly rebalancing, 241 trades). The model was trained on 77 symbols (13,014 sequences) and validated on 85 — including 8 unseen semiconductor stocks it generalized to without retraining (zero-shot). This represents actual trading accuracy with fees and slippage—more meaningful than just directional prediction accuracy.'
      },
      {
        question: 'Can I use my API key in my own applications?',
        answer: 'Yes! The API key works with any application. Check our API documentation for endpoint details and examples.'
      }
    ],
    showBestPractices: false
  }
}

// Get current page help content
const currentPageHelp = computed((): PageHelp => {
  let routeName = route.name?.toString().toLowerCase() || 'dashboard'
  
  // Handle special cases
  if (routeName === 'stockdetail') {
    routeName = 'stocks' // Stock detail page uses same help as stock search
  }
  
  return pageHelpContent[routeName] || pageHelpContent.dashboard
})

// Show best practices banner for certain pages
const showBestPractices = computed(() => currentPageHelp.value.showBestPractices)

const toggleItem = (index: number) => {
  const idx = expandedItems.value.indexOf(index)
  if (idx > -1) {
    expandedItems.value.splice(idx, 1)
  } else {
    expandedItems.value.push(index)
  }
}
</script>
